import { ApolloQueryResult } from 'apollo-client';
import gql from 'graphql-tag';
import {
  Article,
  SingleArticleQuery as SingleArticleQueryType,
  UpNextArticle,
} from './types';
import { singleArticle } from './transformer';
import { getContentfulClient } from 'graphql/clients';
import { getRelatedArticles } from './RelatedArticlesQueries/RelatedArticlesData';
import { getArticleIdsForCategory } from 'data/global/data';
import { UpNextArticleSlugQuery } from 'graphql/__generated__/operations/UpNextArticleSlugQuery';
import {
  UpNextArticleQuery,
  UpNextArticleQuery_upNextArticle_items,
  UpNextArticleQuery_upNextArticle_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/UpNextArticleQuery';
import { relatedArticleImage } from './helpers';
import { ArticleBodyEntriesFragment } from 'data/global/fragments/ArticleBodyEntriesFragment';

const SingleArticleQuery = gql`
  ${ArticleBodyEntriesFragment}

  # Valid example: { "slug": "automate-wordpress", "preview": true }
  query SingleArticleQuery($slug: String!, $preview: Boolean!) {
    articleCollection(where: { slug: $slug }, limit: 1, preview: $preview) {
      items {
        discoverable
        slug
        metadata {
          seoTitle
          seoDescription
          socialImage {
            image {
              url
            }
          }
        }
        relatedArticles: relatedArticlesHardcodedCollection(limit: 5) {
          items {
            slug
            # TODO: Investigate content model migraiton aspect of this.
            #   This field is still pointing to articleAssembly (note: field is largely unused)
            #   See https://zapier.slack.com/archives/CJ6CM1MF0/p1617901887179700?thread_ts=1617901775.179600&cid=CJ6CM1MF0
            title
            shortDescription
            categoriesCollection {
              items {
                title
              }
            }
            primaryImage {
              maxWidth
              describedImage
              image {
                title
                url
              }
            }
          }
        }
        sys {
          id
        }
        title
        subtitle
        shortDescription
        primaryImage {
          maxWidth
          describedImage
          image {
            title
            url
          }
          caption {
            json
          }
        }
        body {
          links {
            entries {
              ...ArticleBodyEntriesFragment
            }
          }
          json
        }
        authorsCollection {
          items {
            displayName
            email
            primaryImage {
              maxWidth
              describedImage
              image {
                title
                url
              }
            }
            biographyShort
          }
        }
        editorialLastPublishedDateComputed
        editorialOriginalPublishDate
        categoriesCollection {
          items {
            sys {
              id # ctg-productivity
            }
            title
            parentCategory {
              title
              linkedFrom {
                categoryAssemblyCollection(limit: 1) {
                  items {
                    slug
                  }
                }
              }
            }
            linkedFrom {
              categoryAssemblyCollection(limit: 1) {
                items {
                  slug
                  category {
                    title
                  }
                }
              }
            }
          }
        }
        readTimeComputed
        readTimeHandcoded
        tagsCollection {
          items {
            title
            slug
          }
        }
        primaryIntegration {
          slug
        }
        integrationsMentionedCollection {
          items {
            slug
            title
          }
        }
        integrationCategoriesCollection {
          items {
            slug
          }
        }
        personasCollection {
          items {
            slug
          }
        }
      }
    }
  }
`;

async function fetchSingleArticle({
  slug,
  preview,
}: {
  slug: string;
  preview: boolean;
}) {
  const ContentfulClient = getContentfulClient(preview);

  const {
    data,
  }: ApolloQueryResult<SingleArticleQueryType> = await ContentfulClient.query({
    query: SingleArticleQuery,
    variables: {
      slug,
      preview,
    },
    errorPolicy: 'none',
  });

  return data;
}

export async function transformContentfulArticle(
  contentfulArticle: SingleArticleQueryType,
) {
  const fetchedArticle = contentfulArticle?.articleCollection?.items?.[0];

  if (!fetchedArticle) {
    throw Error('Post not found - this should never happen!');
  }

  const articleCategory = fetchedArticle?.categoriesCollection?.items?.[0];

  if (articleCategory) {
    // Get the singleArticle's ID so that we can use it to query for related articles
    const fetchedArticleCategorySlug =
      articleCategory.linkedFrom?.categoryAssemblyCollection?.items[0]?.slug ??
      '';

    //  Get 5 related articles using the IDs returned from the getRelatedArticleIds query
    const relatedArticleIds = await getArticleIdsForCategory({
      slug: fetchedArticleCategorySlug,
      limit: 5,
      excludeIds: fetchedArticle.sys.id,
    });

    // Get 5 related articles using the IDs returned from the getRelatedArticleIds query
    const relatedArticleData = await getRelatedArticles(relatedArticleIds);

    return singleArticle(fetchedArticle, relatedArticleData);
  }

  return singleArticle(fetchedArticle);
}

/**
 * Get a single article by slug
 */
export const getSingleArticle = async (
  slug: string,
  preview: boolean,
): Promise<Article> => {
  const contentfulArticle = await fetchSingleArticle({ slug, preview });
  const singleArticle = await transformContentfulArticle(contentfulArticle);

  return singleArticle;
};

const UpNextArticleIdFromSlugQuery = gql`
  query UpNextArticleSlugQuery($slug: String!) {
    upNextSlug: articleCollection(where: { slug: $slug }, limit: 1) {
      items {
        sys {
          id
        }
      }
    }
  }
`;

const UpNextArticleFromIdQuery = gql`
  query UpNextArticleQuery($articleId: String!) {
    upNextArticle: articleCollection(
      where: { sys: { id: $articleId }, discoverable: true }
      order: [editorialLastPublishedDateComputed_DESC]
      limit: 1
    ) {
      items {
        shortDescription
        title
        slug
        categoriesCollection(limit: 2) {
          items {
            title
            parentCategory {
              sys {
                id
              }
            }
            linkedFrom {
              categoryAssemblyCollection {
                items {
                  slug
                }
              }
            }
          }
        }
        primaryImage {
          describedImage
          image {
            url
            title
          }
          maxWidth
        }
      }
    }
  }
`;

const fetchUpNextArticle = async (slug: string) => {
  const contentfulClient = getContentfulClient(false);
  const {
    data: articleIdFromSlug,
  }: ApolloQueryResult<UpNextArticleSlugQuery> = await contentfulClient.query({
    query: UpNextArticleIdFromSlugQuery,
    variables: {
      slug,
    },
    errorPolicy: 'none',
  });

  const articleId = articleIdFromSlug.upNextSlug!.items[0]!.sys.id;

  const {
    data: { upNextArticle },
  }: ApolloQueryResult<UpNextArticleQuery> = await contentfulClient.query({
    query: UpNextArticleFromIdQuery,
    variables: {
      articleId,
    },
    errorPolicy: 'none',
  });

  return upNextArticle!;
};

const getchildCategory = (
  categories: Array<UpNextArticleQuery_upNextArticle_items_categoriesCollection_items>,
) => {
  const parentCategory = categories.find(
    (category) => category.parentCategory?.sys.id,
  )!;

  return parentCategory.title!;
};

function transformUpNextArticle(
  article: UpNextArticleQuery_upNextArticle_items,
): UpNextArticle {
  return {
    image: relatedArticleImage(article.primaryImage),
    category: getchildCategory(
      article.categoriesCollection!
        .items as UpNextArticleQuery_upNextArticle_items_categoriesCollection_items[],
    ),
    shortDescription: article.shortDescription!,
    slug: article.slug!,
    title: article.title!,
  };
}

export const getUpNextArticle = async (slug: string) => {
  const upNextArticle = await fetchUpNextArticle(slug);

  return transformUpNextArticle(upNextArticle.items[0]!);
};
